import React from 'react'
import Accordion from '../../Accordion/Accordion'
import AccordionChild from '../../AccordionChild/AccordionChild'
import termToLowerCasePreservingAcronyms from '../../../domain/term/termToLowerCasePreservingAcronyms'
import { generateCommaSeparatedListEndingWithAnd } from '../../../utils/generateCommaSeparatedListEndingWithAnd'
import {
  isFacetPageType,
  isTaxonomyPageType,
  isConditionPageType,
  isClientelePageType
} from '../../../domain/useCases/pageTypeCheck'
import { SearchState } from '@/types/models'
import { PageType } from '@/types/models/PageType.model'

type Term = {
  fieldValue: string
}

type Props = {
  quick_links: []
  staticHits: []
  pageType: PageType
  termData: {
    title?: string
  }
  terms?: {
    amenities?: Term[]
    options?: Term[]
    therapies?: Term[]
  }
  searchState: SearchState
  searchResults: {}
  pageData: {
    title?: string
  }
  facetData: {
    facet?: string
  }
  shouldDefaultRegionalize?: boolean
  centerCount: number
  termTitle: string
  customTermHeader: string
  compact?: boolean
}

const DefaultTaxonomyFAQs: React.FC<Props> = ({
  quick_links,
  staticHits,
  pageType,
  searchState,
  searchResults,
  termData,
  terms = {},
  pageData: { title = '' },
  facetData,
  shouldDefaultRegionalize = false,
  centerCount,
  termTitle,
  customTermHeader,
  compact,
  ...rest
}) => {
  const { amenities = [], options = [], therapies = [] } = terms

  const isFacet = isFacetPageType(pageType)
  const isTaxonomy = isTaxonomyPageType(pageType)
  const taxonomyTitle = isFacet ? termData?.title : title
  const taxonomyTerm = termToLowerCasePreservingAcronyms(taxonomyTitle)
  const CENTER_COUNT_THRESHOLD = 8
  const isConditionPage = isConditionPageType(pageType)
  const isClientelePage = isClientelePageType(pageType)

  const faqSchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `How can I find a top-rated ${
          shouldDefaultRegionalize ? 'centre' : 'center'
        }?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `1. Ask friends or family members for personal recommendations. 2. Let us help! Our directory includes ${centerCount} top-rated ${
            shouldDefaultRegionalize ? 'centres' : 'centers'
          } renowned for their clinical practice and patient experience. You can review photos, read reviews, and contact a ${
            shouldDefaultRegionalize ? 'centre' : 'center'
          } to start your path to recovery. 3. Ask a trusted doctor or therapist for a referral.`
        }
      },
      {
        '@type': 'Question',
        name: 'How do I know if I need rehab?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'If you have a mental health condition or addiction that hinders your ability to function in your day-to-day life, looking into rehab could be a good idea. Residential rehab also offers a retreat from your normal environment that allows you to focus entirely on healing, which many people find beneficial even if their conditions are less severe.'
        }
      },
      {
        '@type': 'Question',
        name: `How do I choose the best ${
          shouldDefaultRegionalize ? 'centre' : 'center'
        } for treatment?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Consider the level of care (i.e. Detox, Inpatient, or Outpatient)'
        }
      },
      {
        '@type': 'Question',
        name: `What's the typical cost for treatment?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Since many providers <a href='https://recovery.com/price/insurance-accepted/'>accept insurance</a>, rehab doesn't have to be expensive. If you don't have insurance but are able to travel, you can find <a href='/price/budget/'>affordable cash pay rehab ${
            shouldDefaultRegionalize ? 'centres' : 'centers'
          } </a> in destinations around the world.`
        }
      },
      {
        '@type': 'Question',
        name: `What is treatment like at a rehab ${
          shouldDefaultRegionalize ? 'centre' : 'center'
        }?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Rehab facilities often employ evidence-based approaches and offer personalized treatment plans. You can expect to engage in one-on-one counseling sessions and group therapy activities.`
        }
      },
      {
        '@type': 'Question',
        name: `What should I expect in rehab?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Treatment{' '}
          ${
            shouldDefaultRegionalize ? 'centres' : 'centers'
          } offer evidence-based therapies, holistic treatments, and may offer various wellness services.`
        }
      }
    ]
  }

  const faqMarkupString = JSON.stringify(faqSchemaMarkup)

  return (
    <div>
      <script
        dangerouslySetInnerHTML={{ __html: faqMarkupString }}
        type="application/ld+json"
      />
      <Accordion>
        {centerCount > 0 && (
          <AccordionChild
            heading={
              isConditionPage || isClientelePage
                ? `How can I find a top-rated ${
                    shouldDefaultRegionalize ? 'centre' : 'center'
                  } treating ${taxonomyTerm}?`
                : `How can I find a top-rated ${
                    shouldDefaultRegionalize ? 'centre' : 'center'
                  } for treatment?`
            }
            as={'h4'}
            compact={compact}
          >
            <ol className="list-decimal pl-4">
              <li className="mb-4">
                <p className="mb-2">
                  Ask friends or family members for personal recommendations.
                </p>
              </li>
              <li className="mb-4">
                <p className="mb-2">
                  Let Recovery.com help! Our directory includes{' '}
                  <a
                    href="#browse-all"
                    className="font-semibold underline"
                  >
                    {staticHits?.length} top-rated{' '}
                    {shouldDefaultRegionalize ? 'centres' : 'centers'}
                  </a>{' '}
                  renowned for their clinical practice and patient experience.
                  You can review photos, read reviews, and contact a{' '}
                  {shouldDefaultRegionalize ? 'centre' : 'center'} to start your
                  path to recovery.
                </p>
              </li>
              <li className="mb-4">
                <p className="mb-2">
                  Ask a trusted doctor or therapist for a referral.
                </p>
              </li>
            </ol>
          </AccordionChild>
        )}
        <AccordionChild
          heading={'How do I know if I need rehab?'}
          as={'h4'}
          compact={compact}
        >
          If you have a mental health condition or addiction that hinders your
          ability to function in your day-to-day life, looking into rehab could
          be a good idea. Residential rehab also offers a retreat from your
          normal environment that allows you to focus entirely on healing, which
          many people find beneficial even if their conditions are less severe.
        </AccordionChild>

        <AccordionChild
          heading={
            isConditionPage || isClientelePage
              ? `How do I choose the best ${
                  shouldDefaultRegionalize ? 'centre' : 'center'
                } for ${taxonomyTerm} treatment?`
              : `How do I choose the best ${
                  shouldDefaultRegionalize ? 'centre' : 'center'
                } for treatment?`
          }
          as={'h4'}
          compact={compact}
        >
          <ul>
            <li className="disc">
              <p>
                <span className="font-semibold">
                  Consider the level of care
                </span>{' '}
                (i.e. Detox, Inpatient, or Outpatient)
              </p>
            </li>
            <br />
            <li>
              <p>
                <span className="font-semibold">Location</span>: Some
                individuals benefit from traveling farther from where they live
                while others might have family or loved ones more involved in
                their healing process.
              </p>
            </li>
            <br />
            <li>
              <p>
                <span className="font-semibold">Cost</span>: Consider your
                ability to pay through insurance, self-pay, or government-funded
                programs.
              </p>
            </li>
            <br />
            <li>
              <p>
                <span className="font-semibold">Program length</span>: This
                depends on the severity of the patient’s condition.
              </p>
            </li>
            <br />
            <li>
              <p>
                <span className="font-semibold">
                  {shouldDefaultRegionalize ? 'Centre' : 'Center'}{' '}
                  specializations
                </span>
                : Make sure your rehab has licensed clinicians with experience
                specifically treating a certain condition and that they can
                customize your treatment plan to fit your needs.
              </p>
            </li>
          </ul>
        </AccordionChild>
        <AccordionChild
          heading={
            isConditionPage || isClientelePage
              ? `What's the typical cost for ${taxonomyTerm} treatment?`
              : `What's the typical cost for treatment?`
          }
          as={'h4'}
          compact={compact}
        >
          <p>
            Since many providers{' '}
            <a
              className="font-semibold underline"
              href="/price/insurance-accepted/"
            >
              accept insurance
            </a>
            , rehab doesn't have to be expensive. If you don't have insurance
            but are able to travel, you can find{' '}
            <a
              className="font-semibold underline"
              href="/price/budget/"
            >
              affordable cash pay rehab{' '}
              {shouldDefaultRegionalize ? 'centres' : 'centers'}
            </a>{' '}
            in destinations around the world.
          </p>
        </AccordionChild>
        {centerCount > CENTER_COUNT_THRESHOLD &&
          options.length > 0 &&
          therapies.length > 0 && (
            <>
              <AccordionChild
                heading={
                  isConditionPage || isClientelePage
                    ? `What is treatment like at a rehab ${
                        shouldDefaultRegionalize ? 'centre' : 'center'
                      } for ${taxonomyTerm}?`
                    : `What is treatment like at a rehab ${
                        shouldDefaultRegionalize ? 'centre' : 'center'
                      }?`
                }
                compact={compact}
              >
                {isTaxonomy && (
                  <p>
                    The most common treatment approaches are{' '}
                    {generateCommaSeparatedListEndingWithAnd(
                      options
                        .map((option) => option?.fieldValue?.toLowerCase())
                        .filter(Boolean)
                        .slice(0, 5)
                    )}
                    . You might find yourself taking part in{' '}
                    {generateCommaSeparatedListEndingWithAnd(
                      therapies
                        .map((therapy) => therapy?.fieldValue?.toLowerCase())
                        .filter(Boolean)
                        .slice(0, 5)
                    )}
                    .
                  </p>
                )}
                {isFacet && (
                  <p>
                    Rehab facilities often employ evidence-based approaches and
                    offer personalized treatment plans. You can expect to engage
                    in one-on-one counseling sessions and group therapy
                    activities.
                  </p>
                )}
              </AccordionChild>

              <AccordionChild
                heading={
                  isConditionPage || isClientelePage
                    ? `What should I expect in a rehab ${
                        shouldDefaultRegionalize ? 'centre' : 'center'
                      } for ${taxonomyTerm}?`
                    : `What should I expect in a rehab ${
                        shouldDefaultRegionalize ? 'centre' : 'center'
                      }?`
                }
                compact={compact}
              >
                {isTaxonomy && (
                  <p>
                    {title} treatment{' '}
                    {shouldDefaultRegionalize ? 'centres' : 'centers'} often
                    have the following amenities:{' '}
                    {generateCommaSeparatedListEndingWithAnd(
                      amenities
                        .map((amenity) => amenity?.fieldValue?.toLowerCase())
                        .filter(Boolean)
                        .slice(0, 3)
                    )}
                    . Treatment centers offer evidence-based therapies, holistic
                    treatments, and may offer various wellness services.
                  </p>
                )}
                {isFacet && (
                  <p>
                    Treatment {shouldDefaultRegionalize ? 'centres' : 'centers'}{' '}
                    offer evidence-based therapies, holistic treatments, and may
                    offer various wellness services.
                  </p>
                )}
              </AccordionChild>
            </>
          )}
      </Accordion>
    </div>
  )
}

export default DefaultTaxonomyFAQs
